/**
 * @file document-cells.tsx
 * @description Общие кусочки строки документа (phase19.31, часть 3): пилюля
 * статуса, «вид + номер», пометка «реквизиты не заполнены», основание
 * (заказ или сделка). Одни и те же в реестре, во вкладках «Документы»
 * сделки и заказа и в карточке документа.
 */

import { Link } from "react-router";

import { DOCUMENT_STATUS_META, INLINE_LINK_CLASS, documentBasisText, documentTitle } from "./document-view";
import { StatusPill } from "../../ui/StatusPill";
import type { DocumentDto, DocumentStatus } from "../../../lib/api-crm";
import { cn } from "@/lib/utils";

export function DocumentStatusPill({ status }: { status: DocumentStatus }) {
  return <StatusPill meta={DOCUMENT_STATUS_META[status]} />;
}

/** «Счёт СЧТ-2026-00004»: вид обычным, номер — табличными цифрами. */
export function DocumentTitle({ doc }: { doc: Pick<DocumentDto, "kind" | "number"> }) {
  const { kind, number } = documentTitle(doc);
  return (
    <span className="min-w-0 truncate">
      {kind ? <span className="font-medium">{kind} </span> : null}
      {number ? (
        <span className="font-medium tabular-nums">{number}</span>
      ) : (
        <span className="text-on-surface-variant">· черновик</span>
      )}
    </span>
  );
}

/**
 * Номер для первой линии компактной строки: номер целиком (без вида — вид
 * уходит второй линией, QA-5 D7: на 360 «Счёт СЧТ-2026-012…»); у черновика —
 * «Черновик».
 */
export function DocumentNumber({ doc }: { doc: Pick<DocumentDto, "number"> }) {
  return doc.number ? (
    <span className="font-medium tabular-nums">{doc.number}</span>
  ) : (
    <span className="font-medium">Черновик</span>
  );
}

/** Янтарная пометка: реквизиты продавца — заготовка (ссылка — в полосе над списком). */
export function LegalProfileMark() {
  return <span className="text-[var(--status-attention-fg,var(--warning))]">реквизиты не заполнены</span>;
}

interface BasisProps {
  doc: Pick<DocumentDto, "orderId" | "orderNumber" | "dealId" | "dealName">;
  /** База адресов магазина: `/shop/<slug>`. */
  base: string;
  /** Право открыть заказ / сделку; без него — текстом. */
  canOpenOrder: boolean;
  canOpenDeal: boolean;
  /** Переносить текст (плитка сведений), а не обрезать многоточием (таблица). */
  wrap?: boolean;
}

/** Основание ссылкой (без права — текстом, без связи — «—»). */
export function DocumentBasisLink({ doc, base, canOpenOrder, canOpenDeal, wrap = false }: BasisProps) {
  const text = documentBasisText(doc);
  if (!text) return <span className="text-on-surface-variant">—</span>;
  const href = doc.orderId
    ? canOpenOrder
      ? `${base}/orders/${doc.orderId}`
      : null
    : canOpenDeal && doc.dealId
      ? `${base}/crm/deals/${doc.dealId}`
      : null;
  const textCls = wrap ? "break-words" : "truncate";
  if (!href) return <span className={cn("block", textCls)}>{text}</span>;
  return (
    <Link to={href} title={text} className={INLINE_LINK_CLASS}>
      <span className={cn("min-w-0", textCls)}>{text}</span>
    </Link>
  );
}
